import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, WhatsappLogo, Envelope, Phone, MapPin, Airplane, Shield, Package } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service_type: "general", message: "", preferred_contact: "email",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("OK");
        setFormData({ name: "", email: "", phone: "", service_type: "general", message: "", preferred_contact: "email" });
      }
    } catch (error) {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const examples = [
    { icon: Package, title: t("contact.example_1_title"), description: t("contact.example_1_desc") },
    { icon: Airplane, title: t("contact.example_2_title"), description: t("contact.example_2_desc") },
    { icon: Shield, title: t("contact.example_3_title"), description: t("contact.example_3_desc") },
  ];

  const serviceTypes = [
    { value: "general", label: t("contact.service_other") },
    { value: "deplacements", label: t("contact.service_deplacements") },
    { value: "greet_meet", label: t("contact.service_greet") },
    { value: "securite", label: t("contact.service_securite") },
    { value: "transport_luxe", label: t("contact.service_luxe") },
  ];

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="contact-page">
      <SEO title="Contact" path="/contact" description="Contactez ALTURA pour réserver votre chauffeur privé à Paris. Devis gratuit, réponse rapide. Tél : +33 6 81 96 93 42. Email : contact@alturaconciergerie.com" />
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative">
        <div className="texture-overlay" />
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center max-w-3xl mx-auto">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("contact.hero_subtitle")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A1A1A] leading-none mb-8">
              {t("contact.hero_title_1")} <span className="italic">{t("contact.hero_title_2")}</span>
            </h1>
            <p className="text-[#1A1A1A]/80 text-base lg:text-lg font-light leading-relaxed">{t("contact.form_desc")}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#E5E3DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-4">{t("contact.examples_subtitle")}</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {examples.map((ex, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-[#1A1A1A]/10 p-6">
                <ex.icon size={32} weight="thin" className="text-[#1A1A1A] mb-4" />
                <h3 className="text-[#1A1A1A] text-sm font-medium mb-2">{ex.title}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light">{ex.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDEBE4]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-8">{t("contact.info_title")}</h2>
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <Envelope size={24} weight="light" className="text-[#1A1A1A] mt-1" />
                  <div>
                    <h3 className="text-[#1A1A1A] mb-1">{t("contact.email_label")}</h3>
                    <a href="mailto:contact@alturaconciergerie.com" className="text-[#1A1A1A]/70 font-light hover:text-[#1A1A1A] transition-colors">contact@alturaconciergerie.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={24} weight="light" className="text-[#1A1A1A] mt-1" />
                  <div>
                    <h3 className="text-[#1A1A1A] mb-1">{t("contact.phone_label")}</h3>
                    <a href="tel:+33681969342" className="text-[#1A1A1A]/70 font-light hover:text-[#1A1A1A] transition-colors">+33 6 81 96 93 42</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <WhatsappLogo size={24} weight="light" className="text-[#1A1A1A] mt-1" />
                  <div>
                    <h3 className="text-[#1A1A1A] mb-1">{t("contact.whatsapp_label")}</h3>
                    <a href="https://wa.me/33681969342" target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A]/70 font-light hover:text-[#1A1A1A] transition-colors">{t("contact.whatsapp_text")}</a>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {["Paris", "Londres", "Madrid"].map((city) => (
                  <div key={city} className="bg-white border border-[#1A1A1A]/10 p-4 text-center">
                    <p className="text-[#1A1A1A]/80 text-sm">{city}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-12">
                <h2 className="font-heading text-2xl text-[#1A1A1A] mb-2">{t("contact.form_title")}</h2>
                <p className="text-[#1A1A1A]/70 text-sm mb-8">{t("contact.form_desc")}</p>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("contact.name_label")} *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("contact.name_placeholder")} data-testid="input-name" />
                    </div>
                    <div>
                      <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("contact.email_form_label")} *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("contact.email_placeholder")} data-testid="input-email" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("contact.phone_form_label")}</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder={t("contact.phone_placeholder")} data-testid="input-phone" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("contact.service_label")}</label>
                    <select name="service_type" value={formData.service_type} onChange={handleChange} className="input-luxury w-full bg-white cursor-pointer" data-testid="select-service-type">
                      {serviceTypes.map((type) => <option key={type.value} value={type.value} className="bg-white">{type.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("contact.contact_pref")}</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="email" checked={formData.preferred_contact === "email"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <Envelope size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.email")}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="whatsapp" checked={formData.preferred_contact === "whatsapp"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <WhatsappLogo size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.whatsapp")}</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("contact.message_label")} *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className="input-luxury w-full bg-transparent resize-none" placeholder={t("contact.message_placeholder")} data-testid="input-message" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3 disabled:opacity-50" data-testid="submit-contact">
                    {loading ? t("contact.submitting") : t("contact.submit")}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
